import { hasAppAudience } from "../../access-control/models/access-groups.models";
import { useAuthContext } from "../../auth/state/AuthContext";
import { canAccessVesselCard } from "../../pbi/vessel-card/utils/vessel-card-access.utils";
import { ExpandedSearchRow } from "../../search-vessels/components/ExpandedSearchRow";
import { TimeCharter } from "../../search-vessels/models/search.interfaces";
import { parseSortAndFilterTcs } from "../../search-vessels/services/search-vessels.service";
import { CaretDownIcon, DotIcon, SpeedometerIcon } from "../../shared/Icons";
import { ReactComponent as RemoveVessel } from "../../shared/assets/RemoveVessel.svg";
import { ReactComponent as CaretRight } from "../../shared/assets/caretRight.svg";
import { ReactComponent as ShipLock } from "../../shared/assets/lockedVessel.svg";
import { themeAtom } from "../../shared/atoms";
import Rating from "../../shared/components/Rating";
import { VesselDetails } from "../models/home.interfaces";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const Td = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <td className={twMerge("h-14 p-4", className)}>{children}</td>
);

export function MyVesselsTableRow({
  item,
  index,
  isOpen,
  toggleAccordion,
  formatDate,
  removeUser,
  timeCharters,
}: {
  item: any;
  index: number;
  isOpen: boolean;
  toggleAccordion: (index: number) => void;
  formatDate: (dateString: string) => string;
  removeUser: (event: React.MouseEvent<HTMLSpanElement>, index: number, item: VesselDetails) => Promise<void>;
  timeCharters: TimeCharter[];
}) {
  const theme = useAtomValue(themeAtom);
  const user = useAuthContext().state.user;
  // Keeps direct card links limited to the same testers and role grants as the card page.
  const canViewVesselCard =
    hasAppAudience(user?.accessToken?.groups ?? []) &&
    canAccessVesselCard(user?.accessToken, user?.accessControl) &&
    /^\d{7}$/.test(String(item.IMO_NUMBER ?? ""));

  const truncateText = (text: string | undefined) => {
    const str = text ?? "";
    return str.length > 10 ? `${str.slice(0, 10)}...` : str;
  };
  const { cpDate } = parseSortAndFilterTcs(item.TCCODE_TCSTATUS_CPDELDT_CPREDELDT_VOYAGENO);

  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase() as keyof typeof theme.myVesselsTableRow.status;
    const statusMap = theme.myVesselsTableRow.status as Record<string, { border: string; text: string }>;
    return statusMap[s] ?? statusMap.default;
  };

  return (
    <>
      <tr
        className={twMerge(
          "cursor-pointer border text-left text-xs",
          isOpen && "border-l-2 !border-l-green",
          theme.myVesselsTableRow.row.hover,
          theme.myVesselsTableRow.tableText.primary,
          theme.myVesselsTable.tailwind.tableBorder,
        )}
        key={index}
        onClick={() => toggleAccordion(index)}
      >
        <Td className={twMerge("flex items-center", theme.myVesselsTableRow.row.text)}>
          {isOpen ? (
            <CaretDownIcon className="mr-2 h-3.5 w-3.5" onClick={() => toggleAccordion(index)} />
          ) : (
            <CaretRight className="mr-2 h-3.5 w-3.5" onClick={() => toggleAccordion(index)} />
          )}
          <DotIcon
            width={10}
            height={10}
            style={{
              color: item.OPSLINK_ACTIVE ? theme.myVesselsTableRow.dot.active : theme.myVesselsTableRow.dot.inactive,
            }}
          />
          <Link className="ml-1" to={`/vessel/details/${item.IMO_NUMBER}/${item.VESSEL_CODE}/${item.VESSEL_NAME}`}>
            {item.VESSEL_NAME}
          </Link>
        </Td>
        <Td>
          {["Delivered", "Fixed", "On Subjects"].includes(
            // @ts-expect-error //TODO: Check if TC_STATUS is always defined
            cpDate?.tc_status,
          )
            ? formatDate(
                // @ts-expect-error //TODO: Check if CP_DATE is always defined
                cpDate?.cp_date,
              )
            : "-"}
        </Td>
        <Td>
          <p> {item.IMO_NUMBER}</p>
        </Td>
        <Td>
          <p>{item.VESSEL_TYPE}</p>
        </Td>
        <Td>
          <p>{item.OWNER}</p>
        </Td>
        <Td>
          <p>{item.SUMMER_DWT}</p>
        </Td>
        <Td>
          <div>
            <div className="inline-flex h-4 w-6 items-start justify-start gap-0.5">
              <div className={twMerge("text-xs font-semibold leading-none", theme.myVesselsTableRow.tableText.primary)}>
                {item.RIGHTSHIP_SAFETY_SCORE}
              </div>
              <div
                className={twMerge(
                  "text-[11px] font-normal leading-[11px]",
                  theme.myVesselsTableRow.tableText.secondary,
                )}
              >
                /5
              </div>
            </div>
          </div>
        </Td>
        <Td className="relative overflow-hidden">
          <div>{item.RIGHTSHIP_GHG_RATING && <Rating label={item.RIGHTSHIP_GHG_RATING} />}</div>
        </Td>
        <Td>
          <Tooltip
            id="home-tooltip"
            disableStyleInjection={true}
            style={{
              color: theme.myVesselsTableRow.tooltip.text,
              background: theme.myVesselsTableRow.tooltip.background,
              borderRadius: "8px",
              position: "absolute",
              right: "0",
            }}
            place="left"
          />

          <div className="flex items-center justify-end gap-3">
            {canViewVesselCard && (
              <Link
                to={`/vessel-card/${item.IMO_NUMBER}`}
                aria-label={`Open Vessel Card for ${item.VESSEL_NAME}`}
                data-test="my-vessels-vessel-card-link"
                data-tooltip-id="home-tooltip"
                data-tooltip-html="Open Vessel Card"
                className="rounded text-green focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
                onClick={(event) => event.stopPropagation()}
              >
                <SpeedometerIcon className="h-6 w-6" aria-hidden="true" />
              </Link>
            )}
            {item.FROM_IMOS === "N|Y" ? (
              <span onClick={(event) => removeUser(event, index, item)}>
                <RemoveVessel
                  className="h-6 w-6 cursor-pointer no-underline outline-none"
                  data-tooltip-id="home-tooltip"
                  data-tooltip-html="Remove from My Vessels"
                />
              </span>
            ) : (
              <ShipLock
                className="h-6 w-6 no-underline outline-none"
                data-tooltip-id="home-tooltip"
                data-tooltip-html="Assigned on IMOS can’t be removed"
              />
            )}
          </div>
        </Td>
      </tr>

      {isOpen && (
        <ExpandedSearchRow getStatusColor={getStatusColor} timeCharters={timeCharters} truncateText={truncateText} />
      )}
    </>
  );
}
